




















































import Enumerable from "linq";
import { Component, Mixins } from "vue-property-decorator";
import { BrowserPdfPrinter } from "../../../base/components/system";
import {
  HeaderPane,
  RightSidePane,
  MenuPane,
  NavigationPane,
} from "../../../base/components/panes";
import { DomainRegistry } from "../../domain";
import { EditableFormatSummary } from "../../domain/editableFormat";
import { UnsynchronizedFormatListViewer } from "./viewers";

@Component({
  components: {
    HeaderPane,
    UnsynchronizedFormatListViewer,
    RightSidePane,
    MenuPane,
    NavigationPane,
    BrowserPdfPrinter,
  },
})
export default class UnsynchronizedFormatListView extends Mixins() {

  $refs!: {
    pdfPrinter: BrowserPdfPrinter,
    selector: UnsynchronizedFormatListViewer,
  };
  selectedFormats: EditableFormatSummary[] = [];

  get isSelected(): boolean {
    return !!this.selectedFormats && 0 < this.selectedFormats.length;
  }

  get isSingleSelected(): boolean {
    return (!!this.selectedFormats && this.selectedFormats.length === 1);
  }

  get validSelected(): boolean {
    return this.isSingleSelected && !this.selectedFormats[0].deleted;
  }

  get formatNameWording(): string {
    return this.$wording.format.name;
  }

  async deleteFormat(): Promise<void> {
    if (!this.isSingleSelected) {
      return;
    }

    const format = this.selectedFormats[0];
    if (await this.confirmToDeleteFormat()) {
      const archiveId = await this.getLatestFormatArchiveId(format.id);
      if (format.archiveId !== archiveId && !await this.confirmToDeleteLatestFormat()) {
        return;
      }

      try {
        const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
        await repository.deleteFormat(this.selectedFormats[0].id);
        this.refresh();
        this.$successAsHTML(
          "削除完了"
          , "<div>選択した" + this.formatNameWording + "を削除しました。</div>");
      } catch (e) {
        this.$error(
          "削除失敗"
          , "選択した" + this.formatNameWording + "の削除ができませんでした。");
        console.error(e);
      }
    }
  }

  private refresh(): void {
    this.$refs.selector.updateList();
  }

  private async confirmToDeleteFormat(): Promise<boolean> {
    const index = await this.$commonDialogs.confirmation.show({
      message: [
        this.formatNameWording + "を削除します。",
        "本当によろしいですか？",
      ],
      buttons: [
        {
          kind: "confirm",
          label: "中止する",
        },
        {
          kind: "danger",
          label: "削除する",
        },
      ],
    });
    return (index === 1);
  }

  private async confirmToDeleteLatestFormat(): Promise<boolean> {
    const index = await this.$commonDialogs.confirmation.show({
      message: [
        "選択中の" + this.formatNameWording + "より新しい版の" + this.formatNameWording + "が存在します。",
        "最新版の" + this.formatNameWording + "を削除しますか？",
      ],
      buttons: [
        {
          kind: "confirm",
          label: "中止する",
        },
        {
          kind: "danger",
          label: "削除する",
        },
      ],
    });
    return (index === 1);
  }

  previewDocument(): void {
    this.$openWindow(
      "/PreviewNewDocument",
      {
        formatId: this.selectedFormats[0].id.toString(),
        archiveId: this.selectedFormats[0].archiveId.toString(),
      }
    );
  }

  printFormat(): void {
    this.$refs.pdfPrinter.printSource(this.selectedFormats[0].previewSource.source);
  }

  createFormat(): void {
    this.$openWindow(
      "/EditNewFormat",
    );
  }

  async editFormat(): Promise<void> {
    if (!this.isSingleSelected) {
      return;
    }
    const format = this.selectedFormats[0];
    const latestArchiveId = await this.getLatestFormatArchiveId(format.id);
    if (!latestArchiveId
      || (format.archiveId !== latestArchiveId) && !await this.confirmToEditFormat()) {
      return;
    }
    this.$openWindow(
      "/EditFormat",
      {
        formatId: this.selectedFormats[0].id.toString(),
      }
    );
  }

  private async confirmToEditFormat(): Promise<boolean> {
    const index = await this.$commonDialogs.confirmation.show({
      message: [
        "選択中の" + this.formatNameWording + "より新しい版の" + this.formatNameWording + "が存在します。",
        "最新版の" + this.formatNameWording + "を編集しますか？",
      ],
      buttons: [
        {
          kind: "confirm",
          label: "編集する",
        },
        {
          kind: "danger",
          label: "中止する",
        },
      ],
    });
    return (index === 0);
  }

  private async getLatestFormatArchiveId(formatId: number): Promise<number> {
    try {
      const formats = await DomainRegistry.instance.editableFormat.getFormatRepository().getFormatArchives(formatId);
      return Enumerable.from(formats).max(s => s.archiveId);
    } catch (error) {
      this.$error(
        this.formatNameWording + "履歴取得失敗",
        this.formatNameWording + "履歴を取得できませんでした。",
        error,
      );
      return null;
    }
  }

  createCopiedFormat(): void {
    this.$openWindow(
      "/EditNewCopiedFormat",
      {
        formatId: this.selectedFormats[0].id.toString(),
        archiveId: this.selectedFormats[0].archiveId.toString(),
      }
    );
  }

  showFormat(): void {
    this.$openWindow(
      "/ShowFormat",
      {
        formatId: this.selectedFormats[0].id.toString(),
        archiveId: this.selectedFormats[0].archiveId.toString(),
      }
    );
  }

  async synchronizeFormats(): Promise<void> {
    if (await this.confirmToSynchronizeFormats()) {
      try {
        const repository = DomainRegistry.instance.editableFormat.getFormatRepository();
        await repository.synchronizeFormats(this.selectedFormats);
        this.refresh();
        this.$successAsHTML(
          "同期完了"
          , "<div>選択した" + this.formatNameWording + "の同期が完了しました。</div>");
      } catch (e) {
        this.$error(
          "error"
          , "選択した" + this.formatNameWording + "を同期することができませんでした。");
        console.error(e);
      }
    }
  }

  private async confirmToSynchronizeFormats(): Promise<boolean> {
    const index = await this.$commonDialogs.confirmation.show({
      message: [
        "選択している" + this.selectedFormats.length + "件の" + this.formatNameWording + "の同期します。",
        "本当によろしいですか？"
      ],
      buttons: [
        {
          kind: "confirm",
          label: "同期する",
        },
        {
          kind: "danger",
          label: "中止する",
        },
      ],
    });
    return (index === 0);
  }
}
